/**
 * Cloud Run API client (onRequest + CORS). All endpoints are POST with JSON body.
 * Auth-required endpoints send Authorization: Bearer <idToken>.
 */
import { auth } from '../../config/firebase/auth';

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

async function getIdToken(): Promise<string | null> {
  const user = auth?.currentUser ?? null;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

export interface CallableResponse<T = unknown> {
  data: T;
}

async function callCloudRun<T, R>(
  urlEnvKey: string,
  body: T,
  options: { requireAuth?: boolean } = {}
): Promise<CallableResponse<R>> {
  const raw = process.env[urlEnvKey];
  if (!raw) {
    throw new Error(`Missing env: ${urlEnvKey}. Set the Cloud Run URL for this function.`);
  }
  const url = normalizeUrl(raw);
  const token = options.requireAuth ? await getIdToken() : null;
  if (options.requireAuth && !token) {
    throw new Error('Must be logged in');
  }
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error?.message ?? data?.message ?? `Request failed: ${res.status}`;
    const err = new Error(msg) as Error & { code?: string };
    err.code = data?.error?.code;
    throw err;
  }
  return { data: data as R };
}

// Env keys for Cloud Run URLs (no trailing slash)
const URL_KEYS = {
  RETRY_MESA_SYNC: 'REACT_APP_RETRY_MESA_SYNC_URL',
  SYNC_MESA_FROM_POS: 'REACT_APP_SYNC_MESA_FROM_POS_URL',
  CLOSE_MESA: 'REACT_APP_CLOSE_MESA_URL',
  CONFIRM_PAYMENT: 'REACT_APP_CONFIRM_PAYMENT_URL',
  PROCESS_PAYMENT: 'REACT_APP_PROCESS_PAYMENT_URL',
  MARK_BILL_PAID_IN_POS: 'REACT_APP_MARK_BILL_PAID_IN_POS_URL',
} as const;

export function retryMesaSync(payload: { mesaId: string }) {
  return callCloudRun<typeof payload, { success?: boolean; mode?: string; syncResult?: string; [key: string]: unknown }>(
    URL_KEYS.RETRY_MESA_SYNC,
    payload,
    { requireAuth: true }
  );
}

export function syncMesaFromPOS(payload: { mesaId: string }) {
  return callCloudRun<typeof payload, { success?: boolean; mesaId?: string; itemCount?: number; total?: number; error?: string; [key: string]: unknown }>(
    URL_KEYS.SYNC_MESA_FROM_POS,
    payload,
    { requireAuth: true }
  );
}

export function closeMesa(payload: {
  restaurantId: string;
  mesaId: string;
  /** Manual mode only: close mesa and free table even if restante > 0 */
  forceManualEarlyClose?: boolean;
}) {
  return callCloudRun<typeof payload, { success?: boolean; closureId?: string }>(
    URL_KEYS.CLOSE_MESA,
    payload,
    { requireAuth: true }
  );
}

export function confirmPayment(payload: {
  restaurantId: string;
  mesaId: string;
  selectedItemIds?: string[];
  subtotal: number;
  email?: string;
}) {
  return callCloudRun<typeof payload, unknown>(URL_KEYS.CONFIRM_PAYMENT, payload);
}

export function processPayment(payload: unknown) {
  return callCloudRun<unknown, unknown>(URL_KEYS.PROCESS_PAYMENT, payload);
}

export function markBillPaidInPOS(payload: { mesaId: string; paymentMethod: string; amountPaid: number }) {
  return callCloudRun<typeof payload, unknown>(URL_KEYS.MARK_BILL_PAID_IN_POS, payload, { requireAuth: true });
}
